// Text diff engine. Produces the locked compare() contract
// { added, removed, modified, unchanged, changes } (+ text-specific `moved`)
// and a display-ready `render` payload the UI paints without re-splitting text.
import { lineDiff, splitLines } from './line-diff.js';
import { makeNormalizer, lineSimilarity } from './normalize.js';
import { detectMoves } from './move-detect.js';
import { inlineDiff } from './inline-diff.js';

// A del-line paired with an ins-line at the same spot is only an in-place edit
// if the two lines actually share tokens; otherwise it's an unrelated
// remove + add (DEBUGGING.md RCA-001 — same boilerplate trap as move matching).
const MODIFY_MIN_SIM = 0.34;

export function diffText(payload = {}) {
  const { left = '', right = '', options = {} } = payload;
  const L = splitLines(left);
  const R = splitLines(right);
  const norm = makeNormalizer(options);

  const ops = lineDiff(L, R, options);
  const { moves, delBlocks, insBlocks } = detectMoves(ops, L, R, norm);
  const delByStart = new Map(delBlocks.map((b) => [b.opStart, b]));
  const insByStart = new Map(insBlocks.map((b) => [b.opStart, b]));
  moves.forEach((m, i) => (m.id = i));

  const segments = [];
  const changes = [];
  const counts = { added: 0, removed: 0, modified: 0, unchanged: 0 };

  // With "ignore whitespace" on, a line that is only whitespace carries no
  // meaning — blank-line churn is noise, not an add/remove. Such lines still
  // render (neutrally) but don't count or produce a change.
  const ignorableBlank = (text) => options.ignoreWhitespace && text.trim() === '';

  const addSeg = (rIdx) => {
    if (ignorableBlank(R[rIdx])) {
      segments.push({ type: 'add', lnR: rIdx + 1, rText: R[rIdx], ignored: true });
      return;
    }
    segments.push({ type: 'add', lnR: rIdx + 1, rText: R[rIdx] });
    changes.push({ kind: 'add', lineR: rIdx + 1, text: R[rIdx] });
    counts.added++;
  };
  const removeSeg = (lIdx) => {
    if (ignorableBlank(L[lIdx])) {
      segments.push({ type: 'remove', lnL: lIdx + 1, lText: L[lIdx], ignored: true });
      return;
    }
    segments.push({ type: 'remove', lnL: lIdx + 1, lText: L[lIdx] });
    changes.push({ kind: 'remove', lineL: lIdx + 1, text: L[lIdx] });
    counts.removed++;
  };

  let i = 0;
  while (i < ops.length) {
    const op = ops[i];

    if (op.op === 'equal') {
      segments.push({ type: 'context', lnL: op.l + 1, lnR: op.r + 1, lText: L[op.l], rText: R[op.r] });
      counts.unchanged++;
      i++;
      continue;
    }

    if (op.op === 'del') {
      const block = delByStart.get(i);
      if (block.move) {
        emitMoveOrigin(block.move, segments, removeSeg);
        i = block.opEnd + 1;
        continue;
      }
      // Non-move deletion — pair with a following non-move insertion as an
      // in-place modification; leftovers become pure removes/adds.
      const nextIns = insByStart.get(block.opEnd + 1);
      if (nextIns && !nextIns.move) {
        const pairCount = Math.min(block.lines.length, nextIns.lines.length);
        for (let k = 0; k < pairCount; k++) {
          const lIdx = block.lines[k];
          const rIdx = nextIns.lines[k];
          if (lineSimilarity(norm(L[lIdx]), norm(R[rIdx])) >= MODIFY_MIN_SIM) {
            const inline = inlineDiff(L[lIdx], R[rIdx]);
            segments.push({ type: 'modify', lnL: lIdx + 1, lnR: rIdx + 1, lText: L[lIdx], rText: R[rIdx], inline });
            changes.push({ kind: 'change', lineL: lIdx + 1, lineR: rIdx + 1, oldText: L[lIdx], newText: R[rIdx], inline });
            counts.modified++;
          } else {
            // Not a real edit — an unrelated line was removed and another added.
            removeSeg(lIdx);
            addSeg(rIdx);
          }
        }
        for (let k = pairCount; k < block.lines.length; k++) removeSeg(block.lines[k]);
        for (let k = pairCount; k < nextIns.lines.length; k++) addSeg(nextIns.lines[k]);
        i = nextIns.opEnd + 1;
        continue;
      }
      for (const lIdx of block.lines) removeSeg(lIdx);
      i = block.opEnd + 1;
      continue;
    }

    // op.op === 'ins'
    const block = insByStart.get(i);
    if (block.move) {
      emitMoveDest(block.move, segments, changes, counts, addSeg);
      i = block.opEnd + 1;
      continue;
    }
    for (const rIdx of block.lines) addSeg(rIdx);
    i = block.opEnd + 1;
  }

  // Raw view = the naive line-based literal diff, independent of move logic —
  // this is what a line-based tool shows, and the teaching moment.
  const rawRows = ops.map((op) =>
    op.op === 'equal'
      ? { cls: '', ln: op.r + 1, text: R[op.r] }
      : op.op === 'del'
        ? { cls: 'del', ln: op.l + 1, text: L[op.l] }
        : { cls: 'add', ln: op.r + 1, text: R[op.r] },
  );

  return {
    format: 'text',
    ...(left.length && right.length && left.endsWith('\n') !== right.endsWith('\n')
      ? { finalNewline: right.endsWith('\n') ? 'added' : 'removed' } : {}),
    ...counts,
    moved: moves.length,
    changes,
    render: { segments, rawRows, moveCount: moves.length },
  };
}

function moveRanges(move) {
  return {
    fromStart: move.del.lStart + 1,
    fromEnd: move.del.lEnd + 1,
    toStart: move.ins.rStart + 1,
    toEnd: move.ins.rEnd + 1,
  };
}

// Peeled head removes → move-origin marker → peeled tail removes (left order).
function emitMoveOrigin(move, segments, removeSeg) {
  if (move.originEmitted) return;
  move.originEmitted = true;
  for (const lIdx of move.delHead) removeSeg(lIdx);
  segments.push({ type: 'move-origin', id: move.id, ...moveRanges(move), rows: move.originRows });
  for (const lIdx of move.delTail) removeSeg(lIdx);
}

// Peeled head adds → move-dest block → peeled tail adds (right order). Records
// the move change and each interior edit, and updates modified/unchanged.
function emitMoveDest(move, segments, changes, counts, addSeg) {
  for (const rIdx of move.insHead) addSeg(rIdx);

  changes.push({ kind: 'move', ...moveRanges(move), editedCount: move.editedCount });
  for (const inner of move.inner) {
    if (inner.op === 'equal') counts.unchanged++;
    else counts.modified++;
    if (inner.op === 'modify') {
      changes.push({ kind: 'change', lineR: inner.lnR, oldText: inner.lText, newText: inner.rText, inline: inner.inline });
    }
  }
  segments.push({ type: 'move-dest', id: move.id, ...moveRanges(move), editedCount: move.editedCount, rows: move.inner });

  for (const rIdx of move.insTail) addSeg(rIdx);
}
